import {
  Box,
  Button,
  Drawer,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import { DrawerAnnouncement } from "../app/DrawerAnnouncement";
import NoteEditor from "../new-editor/NoteEditor";
import { useToastAlert } from "../app/ToastAlert/ToastAlertProvider";
import { useMutation, useQuery } from "@tanstack/react-query";
import AnnouncementsService from "../../services/AnnouncementsService";
import { DRAWER_ANNOUNCEMENT_QUERY_KEY } from "../app/AppDrawer";

export function AdminAnnouncementsPage() {
  const { data: announcement } = useQuery({
    queryKey: [DRAWER_ANNOUNCEMENT_QUERY_KEY],
    queryFn: AnnouncementsService.getCurrentAnnouncement,
  });

  const [formState, setFormState] = React.useState({
    content: undefined,
    buttonText: "",
    buttonLink: "",
    isActive: false,
    clearAfterDays: 0,
  });

  React.useEffect(() => {
    if (announcement) {
      setFormState({
        content: JSON.parse(announcement?.content || "[]"),
        buttonText: announcement.buttonText,
        buttonLink: announcement.buttonLink,
        isActive: announcement.isActive,
        clearAfterDays: announcement.clearAfterDays || 0,
      });
    }
  }, [announcement]);

  const { showToastAlert } = useToastAlert();

  const { mutate: upsertAnnouncement } = useMutation({
    mutationFn: (variables: any) => {
      return AnnouncementsService.upsertAnnouncement(variables);
    },
    onSuccess: () => {
      showToastAlert("success", {
        message: "Announcement updated successfully!",
      });
    },
    onError: (error) => {
      showToastAlert("error", {
        message: "Announcement updated failed",
      });
      console.error("Error updating announcement:", error);
    },
  });

  const handleUpdate = (field: string) => {
    return (value: any) => {
      setFormState((prev) => ({
        ...prev,
        [field]: value,
      }));
    };
  };

  const handleUpdateToggle = (field: string) => {
    return (value: any) => {
      setFormState((prev) => ({
        ...prev,
        [field]: value.target.checked,
      }));
    };
  };

  const handleUpdateField = (field: string) => {
    return (event: any) => {
      setFormState((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    };
  };

  const handleSave = () => {
    upsertAnnouncement({
      ...formState,
      content: JSON.stringify(formState?.content),
    });
  };

  console.log("Form State:", formState);

  return (
    <Box sx={{ display: "flex", justifyContent: "space-around" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        {formState?.content && (
          <NoteEditor
            value={formState?.content}
            onChange={handleUpdate("content")}
            key={JSON.stringify(announcement?.content)}
          />
        )}
        <TextField
          placeholder="Button Text"
          sx={{ marginTop: 2 }}
          value={formState?.buttonText}
          onChange={handleUpdateField("buttonText")}
        />
        <TextField
          placeholder="Link"
          value={formState?.buttonLink}
          onChange={handleUpdateField("buttonLink")}
        />
        <FormControlLabel
          label="Visible to all coaches"
          control={
            <Switch
              checked={formState?.isActive}
              onChange={handleUpdateToggle("isActive")}
              color="primary"
            />
          }
        />
        <FormControl>
          <FormLabel>Auto clear after: </FormLabel>
          <Select
            value={String(formState?.clearAfterDays)}
            onChange={handleUpdateField("clearAfterDays")}
          >
            <MenuItem value="0">Never</MenuItem>
            <MenuItem value="1">1 day</MenuItem>
            <MenuItem value="3">3 days</MenuItem>
            <MenuItem value="5">5 days</MenuItem>
            <MenuItem value="7">7 days</MenuItem>
            <MenuItem value="14">14 days</MenuItem>
            <MenuItem value="30">30 days</MenuItem>
          </Select>
        </FormControl>
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ textAlign: "center", maxWidth: 300 }}
        >
          Announcements update hourly. Coaches will see changes within an hour.
        </Typography>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={handleSave}
        >
          Save
        </Button>
      </Box>
      <DrawerAnnouncement
        closable={false}
        content={formState?.content}
        buttonText={formState?.buttonText}
        link={formState?.buttonLink}
      />
    </Box>
  );
}
